// src/features/locker-assignments/types/index.ts
import { RaRecord, Identifier } from 'react-admin';

// ============================================
// ENUMS
// ============================================

export enum AssignmentStatus {
  Pending = 'Pending',
  Active = 'Active',
  Archived = 'Archived'
}

// ============================================
// INTERFACES BASE
// ============================================

export interface LockerAssignmentBase {
  id: Identifier;
  hotelId: string;
  displayReference: string;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  roomNumber?: string;
  checkInDate?: string;
  checkOutDate?: string;
  status: AssignmentStatus;
  startsAt: string;
  expiresAt?: string;
  completedAt?: string;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// INTERFACES RELACIONADAS
// ============================================

export interface RequestedLockerType {
  typeId: string;
  typeName?: string;
  requestedCount: number;
  assignedCount?: number;
}

export interface AssignedLocker {
  id: string;
  lockerId?: string;
  displayNumber: string;
  hardwareId?: string;
  lockerTypeId?: string;
  lockerType?: string;
  type?: string; // A veces el backend envía 'type' en lugar de 'lockerType'
  zoneId?: string;
  zone?: string;
  assignedAt?: string;
  releasedAt?: string;
}

export interface HotelSummary {
  id: string;
  name: string;
}

export interface ReferenceSummary {
  key: string;
  value: string;
}

export interface AccessLogEntry {
  id: string;
  assignmentId: string;
  action: string;
  performedBy: string;
  performedByName?: string;
  timestamp: string;
  details?: Record<string, any>;
}

// ============================================
// INTERFACES PARA CONFIGURACIÓN DEL HOTEL
// ============================================

export interface Tag {
  id: string;
  code: string;
  data?: string;
  typeCode: string;
  typeName?: string;
  description?: string;
  status: 'Active' | 'Inactive';
  createdAt?: string;
}

export interface TagType {
  id: string;
  typeCode: string;
  name: string;
  isSystemGenerated: boolean;
  requiresManualInput: boolean;
  description?: string;
}

export interface Hotel {
  id: string;
  name: string;
  location?: string;
  address?: string;
  status?: string;
  supportedTagTypeIds?: string[];
}

export interface LockerType {
  id: string;
  name: string;
  description?: string;
}

export interface Zone {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

export interface ReferenceField {
  key: string;
  label: string;
  isMandatory: boolean;
  fieldType: 'text' | 'number' | 'date';
  defaultValue?: string;
  displayOrder?: number;
}

export interface LockerRequirement {
  lockerTypeId: string;
  typeName: string;
  quantity: number;
}

// ============================================
// INTERFACES PARA LISTA
// ============================================

export interface LockerAssignmentListItem extends LockerAssignmentBase, RaRecord {
  hotel?: HotelSummary;
  totalLockers?: number; // Puede venir o no del backend
  assignedLockers?: number | AssignedLocker[]; // Puede ser número o array
  pendingLockers?: number;
  references?: ReferenceSummary[];
  requestedLockerTypes?: RequestedLockerType[]; // Para calcular el total si no viene
  tagsCount?: number;
  usageSummary?: UsageSummary;
}

// ============================================
// INTERFACES PARA DETALLE
// ============================================

export interface LockerAssignmentDetailResponse extends LockerAssignmentBase, RaRecord {
  hotel?: HotelSummary;
  requestedLockerTypes: RequestedLockerType[];
  assignedLockers: AssignedLocker[];
  references: ReferenceSummary[];
  physicallyAssignedAt?: string;
  physicallyAssignedBy?: string;
  createdByName?: string;
}

// ============================================
// INTERFACES PARA FORMULARIOS
// ============================================

export interface LockerAssignmentFormData {
  hotelId: string;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  roomNumber?: string;
  checkInDate?: string;
  checkOutDate?: string;
  requestedLockerTypes: RequestedLockerType[];
  startsAt?: string;
  expiresAt?: string;
  notes?: string;
  references?: ReferenceSummary[];
}

export interface CreateLockerAssignmentRequest {
  hotelId: string;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  roomNumber?: string;
  checkInDate?: string;
  checkOutDate?: string;
  requestedLockerTypes: RequestedLockerType[];
  startsAt?: string;
  expiresAt?: string;
  notes?: string;
  references: ReferenceSummary[];
}

export interface UpdateLockerAssignmentRequest {
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  roomNumber?: string;
  checkInDate?: string;
  checkOutDate?: string;
  requestedLockerTypes?: RequestedLockerType[];
  expiresAt?: string;
  notes?: string;
  references?: ReferenceSummary[];
}

export interface AssignPhysicalLockersRequest {
  specificLockerIds?: string[];
}

export interface AssignPhysicalLockersResponse extends LockerAssignmentDetailResponse {
  assignedLockers: AssignedLocker[];
  physicallyAssignedAt: string;
  physicallyAssignedBy: string;
}

export interface AssignmentAvailabilityResponse {
  assignmentId: string;
  requestedTypes: RequestedLockerType[];
  availableLockers: {
    typeId: string;
    typeName: string;
    availableCount: number;
    lockers: Array<{
      id: string;
      displayNumber: string;
      zone: string;
    }>;
  }[];
}

export interface AccessLogListResponse {
  data: AccessLogEntry[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export interface UsageSummary {
  accessCount: number;
  lastAccess?: string;
}

// ============================================
// INTERFACES PARA VALIDACIÓN DE FORMULARIOS
// ============================================

export interface ValidationErrors {
  [key: string]: string;
  hotel?: string;
  lockerRequirements?: string;
}

// ============================================
// FUNCIONES HELPER
// ============================================

export const getAssignmentStatusColor = (status: AssignmentStatus): 'success' | 'warning' | 'error' | 'default' | 'info' => {
  switch (status) {
    case AssignmentStatus.Active:
      return 'success';
    case AssignmentStatus.Pending:
      return 'warning';
    case AssignmentStatus.Archived:
      return 'default';
    default:
      return 'default';
  }
};

export const getAssignmentStatusLabel = (status: AssignmentStatus): string => {
  // These will be translated by the component using translate()
  const labels: Record<AssignmentStatus, string> = {
    [AssignmentStatus.Pending]: 'resources.locker-assignments.status.pending',
    [AssignmentStatus.Active]: 'resources.locker-assignments.status.active',
    [AssignmentStatus.Archived]: 'resources.locker-assignments.status.archived',
  };
  return labels[status] || status;
};

export const isAssignmentEditable = (status: AssignmentStatus): boolean => {
  return status === AssignmentStatus.Pending;
};

export const canAssignPhysicalLockers = (status: AssignmentStatus): boolean => {
  return status === AssignmentStatus.Pending || status === AssignmentStatus.Active;
};

export const prepareAssignmentForAPI = (data: LockerAssignmentFormData): CreateLockerAssignmentRequest => {
  return {
    hotelId: data.hotelId,
    guestName: data.guestName || undefined,
    guestEmail: data.guestEmail || undefined,
    guestPhone: data.guestPhone || undefined,
    roomNumber: data.roomNumber || undefined,
    checkInDate: data.checkInDate || undefined,
    checkOutDate: data.checkOutDate || undefined,
    requestedLockerTypes: data.requestedLockerTypes.filter(rt => rt.requestedCount > 0),
    startsAt: data.startsAt || new Date().toISOString(),
    expiresAt: data.expiresAt || undefined,
    notes: data.notes || undefined,
    references: data.references || [],
  };
};

// Helper para obtener el conteo de taquillas asignadas
export const getAssignedLockersCount = (assignedLockers: number | AssignedLocker[] | undefined): number => {
  if (!assignedLockers) return 0;
  if (typeof assignedLockers === 'number') return assignedLockers;
  if (Array.isArray(assignedLockers)) return assignedLockers.length;
  return 0;
};